import { z } from 'zod';
import { MessageRole, GlobalRole, ProjectRole, StatusMessageType, PROJECT_ICON_KEYS, PROJECT_COLOR_KEYS, isKnownScope, isWebhookEventPattern, ANALYTICS_FAMILIES, ANALYTICS_DIMENSIONS, ANALYTICS_EXPORT_FORMATS, STEER_MODES, DEFAULT_STEER_MODE, EFFORT_LEVELS, } from '../types/index.js';
import { ToolCapability } from '../types/tools.js';
/**
 * Экземпляр zod, которым построены **все** схемы этого пакета.
 *
 * В zod 4 `extendZodWithOpenApi` навешивает `.openapi()` на прототип
 * **конкретного** экземпляра модуля (а не глобально). Потребителю, который
 * расширяет zod (публичный OpenAPI в core), нужно расширять ровно тот экземпляр,
 * которым созданы shared-схемы, — иначе у них не будет `.openapi()`.
 */
export { z as sharedZod };
export const messageRoleSchema = z.nativeEnum(MessageRole);
export const globalRoleSchema = z.nativeEnum(GlobalRole);
export const projectRoleSchema = z.nativeEnum(ProjectRole);
export const statusMessageTypeSchema = z.nativeEnum(StatusMessageType);
/** Zod-схемы канонических ключей иконок/цветов (единые для project и session). */
export const projectIconKeySchema = z.enum(PROJECT_ICON_KEYS);
export const projectColorKeySchema = z.enum(PROJECT_COLOR_KEYS);
/** true, если значение — канонический ключ иконки (для записи и чтения). */
export function isProjectIconKey(value) {
    return typeof value === 'string' && PROJECT_ICON_KEYS.includes(value);
}
/** true, если значение — канонический ключ цвета (без алиасов; для записи и чтения). */
export function isProjectColorKey(value) {
    return typeof value === 'string' && PROJECT_COLOR_KEYS.includes(value);
}
/**
 * Нормализация иконки при ЧТЕНИИ. Канонический ключ → как есть; всё прочее
 * (эмодзи/произвольные строки) → `undefined`.
 */
export function normalizeIconKey(value) {
    return isProjectIconKey(value) ? value : undefined;
}
/**
 * Нормализация цвета при ЧТЕНИИ: канонический ключ палитры → как есть; всё
 * прочее (включая legacy-алиасы) → `undefined`.
 */
export function normalizeColorKey(value) {
    return isProjectColorKey(value) ? value : undefined;
}
/**
 * ADR-106 Ф4: нормализация роли участника сессии. Каноническая роль → как
 * есть; всё прочее — **fail-closed** (`viewer`, только чтение).
 */
export function normalizeSessionParticipantRole(value) {
    if (value === 'owner' || value === 'editor' || value === 'viewer')
        return value;
    return 'viewer';
}
export const tokenUsageSchema = z.object({
    inputTokens: z.number().int().min(0),
    outputTokens: z.number().int().min(0),
    totalTokens: z.number().int().min(0),
});
/**
 * Снять у поля патча дефолт (`ZodDefault`/`ZodPrefault`), сохранив валидацию.
 *
 * zod 4 подставляет дефолт даже под `.optional()` (дефолт делает вход
 * опциональным и применяется к `undefined`), поэтому «снять» его можно только
 * развернув обёртку (`unwrap` есть у обоих типов). Пустое значение без обёртки —
 * как есть.
 */
function withoutDefault(field) {
    const type = field._zod?.def?.type;
    if (type !== 'default' && type !== 'prefault')
        return field;
    const unwrap = field.unwrap;
    return typeof unwrap === 'function' ? withoutDefault(unwrap.call(field)) : field;
}
/**
 * Патч-схема объекта: как `.partial()`, но **без дефолтов**.
 *
 * Для PATCH отсутствующий ключ означает «не менять», а `.partial()` в zod 4
 * протекает дефолтами: `pluginsConfigSchema.partial().parse({})` возвращает
 * объект со всеми значениями по умолчанию, и частичный патч затирает чужие
 * настройки. Поэтому поля берутся из `shape`, у каждого снимается дефолт и
 * навешивается `.optional()`. Набор ключей и правила валидации остаются из
 * исходной схемы (единственный источник правды). `.strict()` вызывается
 * потребителем, если нужен отказ на неизвестный ключ.
 */
export function patchObjectSchema(schema) {
    const patchShape = {};
    for (const [key, field] of Object.entries(schema.shape)) {
        patchShape[key] = withoutDefault(field).optional();
    }
    return z.object(patchShape);
}
/**
 * Instance-wide дефолты оптимизации контекста для новых сессий
 * (`context.defaults`, runtime-override — `/api/v2/settings/context-defaults`).
 *
 * Значения по умолчанию совпадают с прежними литералами
 * `SessionManager.createSession`: новая сессия без явных `settings` получает
 * ровно те же настройки, что и до появления настройки инстанса.
 */
export const contextDefaultsSchema = z.object({
    // `sticky_notes` — legacy-значение сессий, в дефолтах и UI не предлагается:
    // заметки работают независимо от стратегии оптимизации.
    contextOptimization: z.enum(['none', 'sliding_window', 'summarization', 'compaction']).default('compaction'),
    summarizationEnabled: z.boolean().default(true),
    summarizationTriggerMode: z.enum(['messages', 'tokens']).default('messages'),
    summarizeAfterN: z.number().int().min(1).default(10),
    summarizeTokenThreshold: z.number().int().min(1).max(100).default(50),
    /** @deprecated Новый движок сжатия не использует head/tail (см. `summarizationKeepTokens`). */
    summarizationHeadMessages: z.number().int().min(1).default(2),
    /** @deprecated См. `summarizationHeadMessages`. */
    summarizationTailMessages: z.number().int().min(1).default(3),
    summarizationKeepTokens: z.number().int().min(0).default(5000),
    compactionKeepTokens: z.number().int().min(0).default(15000),
    compactionTokenThreshold: z.number().int().min(1).max(100).default(85),
    slidingWindowMode: z.enum(['messages', 'tokens']).default('messages'),
    slidingWindowMessageLimit: z.number().int().min(1).default(10),
    slidingWindowTokenThreshold: z.number().int().min(1).max(100).default(80),
});
/**
 * Instance-wide лимиты заметок сессии (`context.stickyNotes`).
 *
 * Не дефолты сессии, а **ограничения инстанса**: применяются ко всем сессиям
 * при создании/изменении заметки. Runtime-override —
 * `/api/v2/settings/sticky-notes`. Значения по умолчанию совпадают с прежним
 * поведением (заметки не ограничивались) и задают разумный верхний предел,
 * чтобы заметки не превращались в бездонный канал контекста.
 */
export const stickyNotesLimitsSchema = z.object({
    maxCount: z.number().int().min(1).max(1000).default(50),
    maxChars: z.number().int().min(1).max(100000).default(2000),
});
export const sessionSettingsSchema = z.object({
    provider: z.string().optional(),
    model: z.string().optional(),
    reasoningLevel: z.enum(['default', 'none', 'low', 'medium', 'high']).optional(),
    /**
     * ADR-117: явный тир усилия (effort) — `xhigh`/`max` задаются только так.
     * Невалидное значение → 400 (`VALIDATION_ERROR`), а не тихий игнор.
     */
    effort: z.enum(EFFORT_LEVELS).optional(),
    // Без дефолта: `undefined` = дефолт модели (поле не отправляется провайдеру).
    temperature: z.number().min(0).max(2).optional(),
    maxTokens: z.number().int().min(0).max(200000).default(0),
    systemPrompt: z.string().optional(),
    debugEnabled: z.boolean().default(true),
    streamEnabled: z.boolean().default(true),
    agentRole: z.string().optional(),
    // ADR-071 §2.4: сессионный скоуп MCP (явно включённые / opt-out).
    mcpServers: z.array(z.string()).optional(),
    mcpDisabledServers: z.array(z.string()).optional(),
    contextOptimization: z.enum(['none', 'sliding_window', 'summarization', 'compaction', 'sticky_notes']).default('none'),
    slidingWindowMode: z.enum(['messages', 'tokens']).default('messages'),
    slidingWindowMessageLimit: z.number().int().min(1).default(10),
    slidingWindowTokenThreshold: z.number().int().min(10).max(100).default(80),
    summarizationEnabled: z.boolean().default(false),
    summarizationTriggerMode: z.enum(['messages', 'tokens']).default('messages'),
    summarizeAfterN: z.number().int().min(1).default(10),
    summarizeTokenThreshold: z.number().int().min(10).max(100).default(50),
    /** @deprecated Новый движок сжатия не использует head/tail (см. `summarizationKeepTokens`). */
    summarizationHeadMessages: z.number().int().min(1).default(2),
    /** @deprecated См. `summarizationHeadMessages`. */
    summarizationTailMessages: z.number().int().min(1).default(3),
    summarizationKeepTokens: z.number().int().min(0).default(5000),
    compactionKeepTokens: z.number().int().min(0).default(15000),
    compactionTokenThreshold: z.number().int().min(1).max(100).default(85),
    showInterview: z.boolean().default(true),
    websocketEnabled: z.boolean().default(true),
    language: z.string().optional(),
    // === Plan/Act (ADR-025) ===
    planMode: z.enum(['auto', 'strict', 'off']).optional(),
    // === Скиллы (ADR-046 §4.5) ===
    skills: z.array(z.string()).optional(),
    skillsDisabled: z.array(z.string()).optional(),
    // === Микро-слой (ADR-035/ADR-084) ===
    /** ADR-084: per-session гейт decision-уровня микро-каскада (undefined → config.decision.enabled). */
    decisionFirst: z.boolean().optional(),
});
export const sessionCreateSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    title: z.string().min(1).max(200).optional(),
    icon: projectIconKeySchema.optional().nullable(),
    color: projectColorKeySchema.optional().nullable(),
    projectId: z.string().optional(),
    settings: sessionSettingsSchema.optional(),
});
export const sessionUpdateSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    icon: projectIconKeySchema.optional().nullable(),
    color: projectColorKeySchema.optional().nullable(),
    projectId: z.string().optional(),
    // passthrough: настройки сессии содержат поля вне sessionSettingsSchema
    // (mcpServers, routing*, microFirst, embedding*, permission, mcpToolSettings, llmGateway),
    // их нельзя терять при PATCH — известные поля валидируются, неизвестные пропускаются.
    // `patchObjectSchema` (а не `.partial()`): отсутствующий ключ не должен получать
    // дефолт из схемы — иначе PATCH сбрасывает настройки, которые не передавали.
    settings: patchObjectSchema(sessionSettingsSchema).passthrough().optional(),
});
/**
 * ADR-076: запись реестра git-репозиториев. `path` обязателен, остальные поля
 * опциональны; старые формы (`string` / `{ path }`) принимаются через
 * `normalizeGitRepoConfig` и union в `repos`.
 */
export const gitRepoConfigSchema = z.object({
    id: z.string().optional(),
    name: z.string().optional(),
    url: z.string().optional(),
    type: z.enum(['https', 'ssh']).optional(),
    path: z.string().min(1),
    defaultBranch: z.string().optional(),
    sshKeyId: z.string().optional(),
    lastFetch: z.string().optional(),
});
/**
 * ADR-076: нормализация legacy-значения `repos` к `GitRepoConfig`.
 * `string` → `{ path }`; объект → прогон через `gitRepoConfigSchema`
 * (невалидный → `null`). Всё прочее → `null`.
 */
export function normalizeGitRepoConfig(raw) {
    if (typeof raw === 'string') {
        return raw.length > 0 ? { path: raw } : null;
    }
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        const parsed = gitRepoConfigSchema.safeParse(raw);
        return parsed.success ? parsed.data : null;
    }
    return null;
}
export const projectCreateSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().default(''),
    icon: projectIconKeySchema.optional().nullable(),
    color: projectColorKeySchema.optional().nullable(),
    workspace: z.string().optional(),
    repos: z.array(z.union([z.string(), gitRepoConfigSchema])).optional(),
});
/** ADR-107: проектная политика памяти (undefined — не менять). */
export const projectMemoryUpdateSchema = z.object({ enabled: z.boolean().optional() });
export const projectUpdateSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().optional(),
    currentTask: z.string().optional(),
    invariants: z.string().optional(),
    icon: projectIconKeySchema.optional().nullable(),
    color: projectColorKeySchema.optional().nullable(),
    workspace: z.string().optional(),
    repos: z.array(z.union([z.string(), gitRepoConfigSchema])).optional(),
    memory: projectMemoryUpdateSchema.optional(),
});
/** ADR-076: тело создания SSH-ключа (`POST /api/v2/ssh-keys`). */
export const sshKeyCreateSchema = z.object({
    name: z.string().min(1).max(200),
    type: z.string().optional(),
    privateKey: z.string().min(1),
    passphrase: z.string().optional(),
    agentIds: z.array(z.string()).optional(),
});
/** ADR-076: тело обновления SSH-ключа (без id/createdAt). */
export const sshKeyUpdateSchema = sshKeyCreateSchema.partial();
/**
 * ADR-071 §2.4: проектный скоуп MCP (ссылки на реестр по имени).
 * PUT-семантика: `undefined` ≠ `[]` — отсутствующее поле сохраняет текущее
 * значение, поэтому `.default([])` здесь недопустим.
 */
export const projectMcpUpdateSchema = z.object({
    enabled: z.array(z.string()).optional(),
    defaultOn: z.array(z.string()).optional(),
});
/**
 * ADR-075: тело `PUT /api/v2/settings/workspace-policy` — политика для
 * абсолютного workspace вне `workspacesRoot`.
 */
export const workspacePolicySchema = z.object({
    outsideRoot: z.enum(['confirm', 'deny']),
});
export const promptRequestSchema = z.object({
    messageId: z.string().optional(),
    content: z.string().min(1),
    stream: z.boolean().default(true),
    model: z.string().optional(),
    provider: z.string().optional(),
    agent: z.string().optional(),
    system: z.string().optional(),
    /**
     * ADR-104: режим отправки. `queue` (по умолчанию) — прежнее поведение;
     * `steer` — инъекция в активный ход без прерывания генерации;
     * `interrupt` — прерывание текущей генерации с продолжением хода.
     */
    mode: z.enum(STEER_MODES).default(DEFAULT_STEER_MODE),
    /**
     * ADR-115: пути объектов вложений в blob-хранилище (`attachments/<sha2>/<sha256>`),
     * полученные из `POST /sessions/:id/attachments`. Аддитивное поле: без него
     * поведение промпта прежнее. Порядок частей сообщения — текст, затем вложения.
     */
    attachments: z.array(z.string().min(1)).max(50).optional(),
});
export const spaLoginSchema = z.object({
    apiKey: z.string().min(1),
    username: z.string().optional(),
});
// --- File System schemas ---
export const fsListQuerySchema = z.object({
    project_id: z.string().min(1),
    path: z.string().default(''),
    maxDepth: z.coerce.number().int().min(1).max(10).optional(),
    includeHidden: z.coerce.boolean().optional(),
});
export const fsReadQuerySchema = z.object({
    project_id: z.string().min(1),
    path: z.string().min(1),
});
export const fsWriteBodySchema = z.object({
    projectId: z.string().min(1),
    path: z.string().min(1),
    content: z.string(),
});
export const fsDeleteQuerySchema = z.object({
    project_id: z.string().min(1),
    path: z.string().min(1),
});
export const fsMkdirBodySchema = z.object({
    projectId: z.string().min(1),
    path: z.string().min(1),
});
export const fsDownloadQuerySchema = z.object({
    project_id: z.string().min(1),
    path: z.string().min(1),
});
/**
 * ADR-075: запрос навигации по корню workspace (`GET /fs/workspace-root/browse`,
 * `GET /fs/workspace-root`). `includeHidden` принимает строковые `true`/`false`
 * (query-параметры) — `z.coerce.boolean()` не подходит, т.к. `'false'` → `true`.
 */
export const fsWorkspaceBrowseQuerySchema = z.object({
    path: z.string().default(''),
    includeHidden: z
        .preprocess((v) => (v === 'true' ? true : v === 'false' ? false : v), z.boolean().optional())
        .default(false),
});
/** ADR-075: тело `POST /fs/workspace-root/mkdir` — создание каталога в корне workspace. */
export const fsWorkspaceMkdirBodySchema = z.object({
    path: z.string().min(1),
    /** `confirm`-политика: явное подтверждение создания каталога вне `workspacesRoot`. */
    confirmOutsideRoot: z.boolean().optional(),
});
// --- Git schemas ---
export const gitScanBodySchema = z.object({
    projectId: z.string().min(1),
});
export const gitInitBodySchema = z.object({
    projectId: z.string().min(1),
    path: z.string(),
    defaultBranch: z.string().optional(),
    name: z.string().optional(),
    sshKeyId: z.string().optional(),
});
export const gitCloneBodySchema = z.object({
    projectId: z.string().min(1),
    url: z.string().min(1),
    path: z.string().optional(),
    branch: z.string().optional(),
    name: z.string().optional(),
    sshKeyId: z.string().optional(),
});
/**
 * ADR-076: тело `POST /api/v2/git/repos/register` — upsert записи реестра
 * (идемпотентно по `path`). Используется UI для добавления уже существующего
 * локально репозитория.
 */
export const gitReposRegisterBodySchema = z.object({
    projectId: z.string().min(1),
    path: z.string().min(1),
    name: z.string().optional(),
    url: z.string().optional(),
    type: z.enum(['https', 'ssh']).optional(),
    defaultBranch: z.string().optional(),
    sshKeyId: z.string().optional(),
});
/**
 * ADR-076: тело `POST /api/v2/git/repos/remove` — удаление записи реестра;
 * обязателен `id` либо `path`. `deleteLocal` — удалить каталог репозитория.
 */
export const gitReposRemoveBodySchema = z
    .object({
    projectId: z.string().min(1),
    id: z.string().optional(),
    path: z.string().optional(),
    deleteLocal: z.boolean().optional(),
})
    .refine((v) => Boolean(v.id || v.path), { message: 'Требуется id или path' });
/** ADR-076: тело `POST /api/v2/git/repos/fetch` — обязателен `id` либо `path`. */
export const gitReposFetchBodySchema = z
    .object({
    projectId: z.string().min(1),
    id: z.string().optional(),
    path: z.string().optional(),
})
    .refine((v) => Boolean(v.id || v.path), { message: 'Требуется id или path' });
export const gitLogQuerySchema = z.object({
    project_id: z.string().min(1),
    repo_path: z.string().default(''),
    max_count: z.coerce.number().int().min(1).max(100).optional(),
});
export const gitCommitQuerySchema = z.object({
    project_id: z.string().min(1),
    repo_path: z.string().default(''),
    hash: z.string().min(1),
});
const toolCapabilityValues = Object.values(ToolCapability);
/**
 * ADR-071 §2.6: фабрика схемы `tools.disabled` (единый источник значений
 * на базе `ToolCapability`).
 *
 * - `strictCapabilities: true` (API, `PUT /settings/tools-disabled`) — закрытый
 *   набор capability: опечатка отклоняется, а не молча ничего не отключает;
 * - `strictCapabilities: false` (config.yaml) — permissive: значения — строки
 *   (конфиг может опережать версию ядра).
 */
export function createToolsDisabledSchema(strictCapabilities) {
    return z.object({
        capabilities: (strictCapabilities
            ? z.array(z.enum(toolCapabilityValues))
            : z.array(z.string())).default([]),
        tools: (strictCapabilities
            ? z.array(z.string().min(1))
            : z.array(z.string())).default([]),
    });
}
/**
 * ADR-071 §2.6: тело `PUT /api/v2/settings/tools-disabled`. Неизвестная
 * capability — ошибка (закрытый набор: опечатка молча ничего не отключит).
 * Имена инструментов принимаются как строки (реестр динамический — MCP-тулы
 * регистрируются лениво); непустые.
 */
export const toolsDisabledSchema = createToolsDisabledSchema(true);
/**
 * ADR-071 §2.4: базовый контракт MCP-скоупа сессии (ссылки на реестр по имени).
 * `undefined` ≠ `[]`: отсутствующее поле сохраняет текущее значение.
 */
export const sessionMcpScopeSchema = z
    .object({
    mcpServers: z.array(z.string()).optional(),
    mcpDisabledServers: z.array(z.string()).optional(),
})
    .refine((v) => v.mcpServers !== undefined || v.mcpDisabledServers !== undefined, { message: 'Требуется mcpServers или mcpDisabledServers' });
/**
 * ADR-071 §2.4: тело `PUT /api/v2/sessions/:id/mcp`: точечный toggle
 * (`{ name, enabled }`, в т.ч. opt-out системного сервера) или полная/частичная
 * замена (`{ mcpServers?, mcpDisabledServers? }`).
 */
export const sessionMcpUpdateSchema = z
    .object({
    name: z.string().min(1).optional(),
    enabled: z.boolean().optional(),
    mcpServers: z.array(z.string()).optional(),
    mcpDisabledServers: z.array(z.string()).optional(),
})
    .refine((v) => v.name !== undefined
    || v.mcpServers !== undefined
    || v.mcpDisabledServers !== undefined, { message: 'Требуется name, mcpServers или mcpDisabledServers' });
// --- Скиллы (ADR-046) ---
/** ADR-046 §3.2: имя скилла — 1–64, `^[a-z0-9]+(-[a-z0-9]+)*$`. */
export const SKILL_NAME_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const SKILL_NAME_MIN = 1;
export const SKILL_NAME_MAX = 64;
/** ADR-046 §3.2: длина `description`. */
export const SKILL_DESCRIPTION_MIN = 1;
export const SKILL_DESCRIPTION_MAX = 1024;
export const skillNameSchema = z
    .string()
    .min(SKILL_NAME_MIN)
    .max(SKILL_NAME_MAX)
    .regex(SKILL_NAME_REGEX, 'имя скилла: строчные латинские буквы/цифры, разделитель — дефис');
export const skillCreateSchema = z.object({
    name: skillNameSchema,
    description: z.string().min(SKILL_DESCRIPTION_MIN).max(SKILL_DESCRIPTION_MAX),
    /** Тело Markdown (без frontmatter). */
    body: z.string().optional(),
    /** Дополнительные supporting-файлы (относительный путь → содержимое). */
    files: z.record(z.string().min(1), z.string()).optional(),
});
export const skillUpdateSchema = z
    .object({
    description: z.string().min(SKILL_DESCRIPTION_MIN).max(SKILL_DESCRIPTION_MAX).optional(),
    body: z.string().optional(),
    /** Толерантный контейнер (неизвестные ключи сохраняются). */
    metadata: z.record(z.string(), z.unknown()).optional(),
})
    .refine((v) => v.description !== undefined || v.body !== undefined || v.metadata !== undefined, { message: 'Требуется description, body или metadata' });
export const skillFileWriteSchema = z.object({
    content: z.string(),
});
/**
 * ADR-046 §4.5: per-session enable/disable скиллов (по образцу MCP-скоупа).
 * `undefined` ≠ `[]`: отсутствующее поле сохраняет текущее значение.
 */
export const skillSessionOverrideSchema = z.object({
    skills: z.array(z.string()).optional(),
    skillsDisabled: z.array(z.string()).optional(),
});
// --- Slash-команды и bundles (ADR-046 §7) ---
/** ADR-046 §7.3: тело `POST/PATCH /api/v2/skill-bundles` (YAML-группа скиллов). */
export const skillBundleSchema = z.object({
    name: skillNameSchema,
    description: z.string().min(SKILL_DESCRIPTION_MIN).max(SKILL_DESCRIPTION_MAX),
    skills: z.array(skillNameSchema).min(1),
    instruction: z.string().optional(),
});
/** ADR-046 §7.4: тело `PUT /api/v2/settings/skills-disabled` (группы/имена). */
export const skillsDisabledSchema = z.object({
    categories: z.array(z.string()).default([]),
    groups: z.array(z.string()).default([]),
    skills: z.array(z.string()).default([]),
});
/**
 * ADR-046 §4.5: тело `PUT /api/v2/settings/skills` — системные настройки:
 * - `disabled` — hard-gate: скиллы, недоступные НИГДЕ;
 * - `disabledForNewProjects` — сид для НОВЫХ проектов (снимок при создании);
 * - `policy` — политика доступности (строкой, валидируется роутом).
 */
export const skillsSystemSettingsSchema = z.object({
    disabled: skillsDisabledSchema.optional(),
    disabledForNewProjects: skillsDisabledSchema.optional(),
    policy: z.string().optional(),
});
/**
 * ADR-046 §4.5: тело `PUT /api/v2/sessions/:id/skills`: точечный toggle
 * (`{ name, enabled }`) или полная/частичная замена (`{ skills?, skillsDisabled? }`).
 */
export const sessionSkillUpdateSchema = z
    .object({
    name: z.string().min(1).optional(),
    enabled: z.boolean().optional(),
    skills: z.array(z.string()).optional(),
    skillsDisabled: z.array(z.string()).optional(),
})
    .refine((v) => v.name !== undefined
    || v.skills !== undefined
    || v.skillsDisabled !== undefined, { message: 'Требуется name, skills или skillsDisabled' });
/** ADR-046 §4.5: тело `PUT /api/v2/projects/:id/skills`. */
export const projectSkillUpdateSchema = z.object({
    enabled: z.array(z.string()).optional(),
    defaultOn: z.array(z.string()).optional(),
});
// --- Внешние события (ADR-089) ---
/** ADR-089: схема идентичности отправителя внешнего события. */
export const eventIdentitySchema = z.object({
    principalId: z.string().optional(),
    chatKey: z.string().optional(),
    threadKey: z.string().optional(),
    roles: z.array(z.string()).optional(),
});
/**
 * ADR-115 × ADR-089: ссылка на вложение внешнего события.
 *
 * `blobPath` ограничен контуром вложений (`attachments/…`) — через событие нельзя
 * сослаться на произвольный объект blob-хранилища (артефакты, логи, скиллы).
 * `sha256` — 64 hex-символа (content-addressed адрес), `mime`/`size` — непустые.
 */
export const eventAttachmentRefSchema = z.object({
    blobPath: z
        .string()
        .min(1)
        .max(512)
        .regex(/^attachments\/[A-Za-z0-9][A-Za-z0-9._/-]*$/, 'blobPath должен быть внутри attachments/')
        // `attachments/../…` формально проходит regex — сегменты `..` запрещены явно
        // (паритет с `isAttachmentPath` ядра: ссылка не должна выходить из контура).
        .refine((value) => !value.includes('..'), 'blobPath не должен содержать «..»'),
    sha256: z.string().regex(/^[0-9a-f]{64}$/, 'sha256 должен быть 64 hex-символа'),
    mime: z.string().min(1).max(255),
    size: z.number().int().positive(),
    name: z.string().min(1).max(200).optional(),
});
/** ADR-089: схема нормализованного внешнего события (`payload` не типизируется). */
export const synthEventSchema = z.object({
    id: z.string().min(1),
    source: z.string().min(1),
    type: z.string().min(1),
    payload: z.unknown(),
    identity: eventIdentitySchema.prefault({}),
    dedupKey: z.string().min(1),
    ts: z.string().min(1),
    // ADR-115 × ADR-089: ссылки на вложения (аддитивно).
    attachments: z.array(eventAttachmentRefSchema).optional(),
});
/** ADR-089: режимы доставки `EventBinding` (без `drop`). */
const eventDeliveryModeSchema = z.enum(['bound', 'ephemeral', 'queue']);
/** ADR-089: схема устойчивой привязки внешнего адреса к сессии/шаблону. */
export const eventBindingSchema = z.object({
    id: z.string().min(1),
    source: z.string().min(1),
    chatKey: z.string().optional(),
    threadKey: z.string().optional(),
    sessionId: z.string().optional(),
    mode: eventDeliveryModeSchema,
    agentTemplate: z.string().optional(),
    project: z.string().optional(),
});
/** ADR-089: схема запроса создания/привязки сессии — единый funnel. */
export const sessionRequestSchema = z.object({
    initiator: z.enum(['user', 'agent', 'external', 'system']),
    intent: z.enum(['explicit', 'event']),
    projectId: z.string().optional(),
    parentId: z.string().optional(),
    agentTemplate: z.string().optional(),
    mode: eventDeliveryModeSchema.optional(),
    ttl: z.string().optional(),
    title: z.string().optional(),
    icon: projectIconKeySchema.optional(),
    color: projectColorKeySchema.optional(),
    sessionOrigin: z.enum(['user', 'fork', 'subagent']).optional(),
    // Настройки сессии валидируются собственной схемой при создании; здесь —
    // толерантный контейнер, чтобы не дублировать SessionSettings.
    settings: z.unknown().optional(),
    sourceEventId: z.string().optional(),
    dedupKey: z.string().optional(),
});
/**
 * ADR-089: вход приёма внешнего события (`POST /api/v2/events`). `id`/`dedupKey`/`ts`
 * нормализуются `EventIngress`, поэтому здесь опциональны.
 *
 * ADR-115 × ADR-089: `attachments[]` — до 10 ссылок на вложения (жёсткий предел;
 * фактический лимит `attachments.maxFilesPerMessage` проверяет ingress). Если
 * вложения переданы, `dedupKey` **обязателен**: ключ идемпотентности по умолчанию
 * строится из `payload` и для бинарного содержимого нестабилен.
 */
const eventIngestBaseSchema = z.object({
    source: z.string().min(1),
    type: z.string().min(1),
    payload: z.unknown().optional(),
    identity: eventIdentitySchema.optional(),
    dedupKey: z.string().min(1).optional(),
    ts: z.string().optional(),
    attachments: z.array(eventAttachmentRefSchema).max(10).optional(),
});
/**
 * ADR-115 × ADR-089: вложения без явного `dedupKey` — отказ на уровне схемы
 * (`400 VALIDATION_ERROR`), иначе дедуп-ключ зависел бы от содержимого вложений.
 */
function requireDedupKeyForAttachments(value, ctx) {
    if (Array.isArray(value.attachments) && value.attachments.length > 0 && !value.dedupKey) {
        ctx.addIssue({
            code: 'custom',
            path: ['dedupKey'],
            message: 'attachments требуют явного dedupKey',
        });
    }
}
export const eventIngestSchema = eventIngestBaseSchema.superRefine(requireDedupKeyForAttachments);
/** ADR-089: вход upsert биндинга (`POST /api/v2/events/bindings`) — без `id`. */
export const eventBindingInputSchema = z.object({
    source: z.string().min(1),
    chatKey: z.string().optional(),
    threadKey: z.string().optional(),
    sessionId: z.string().optional(),
    mode: eventDeliveryModeSchema,
    agentTemplate: z.string().optional(),
    project: z.string().optional(),
});
/**
 * ADR-089/ADR-052: метаданные API-ключа привязки для приёма событий.
 * Секрет (`key`) и его хеш здесь отсутствуют — наружу не отдаются.
 */
export const eventBindingApiKeySchema = z.object({
    id: z.string().min(1),
    bindingId: z.string().min(1),
    label: z.string().min(1),
    description: z.string().optional(),
    rateLimitPerMinute: z.number().int().min(0).optional(),
    createdBy: z.string().min(1),
    createdAt: z.string().min(1),
    lastUsedAt: z.string().optional(),
    prefix: z.string().min(1),
    // ADR-052 (задел): срок действия / отзыв ключа.
    expiresAt: z.string().optional(),
    revokedAt: z.string().optional(),
});
/** ADR-089/ADR-052: тело создания API-ключа привязки — обязательная уникальная `label`. */
export const eventBindingApiKeyCreateSchema = z.object({
    label: z.string().min(1),
    description: z.string().optional(),
    expiresAt: z.string().optional(),
    rateLimitPerMinute: z.number().int().min(0).optional(),
});
/**
 * ADR-089/ADR-052: тело обновления API-ключа привязки. Все поля опциональны;
 * `null` для `description`/`expiresAt`/`rateLimitPerMinute` — сброс значения.
 */
export const eventBindingApiKeyUpdateSchema = z.object({
    label: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    expiresAt: z.string().nullable().optional(),
    rateLimitPerMinute: z.number().int().min(0).nullable().optional(),
});
/** ADR-089: ответ создания ключа — метаданные + plaintext-секрет (единожды). */
export const eventBindingApiKeyCreatedSchema = eventBindingApiKeySchema.extend({
    key: z.string().min(1),
});
/**
 * ADR-089: вход `POST /events` при аутентификации API-ключом привязки. Как
 * `eventIngestSchema`, но `source` опционален — источник берётся из привязки.
 * ADR-115 × ADR-089: правило «вложения требуют `dedupKey`» действует и здесь.
 */
export const eventKeyIngestSchema = eventIngestBaseSchema
    .partial({ source: true })
    .superRefine(requireDedupKeyForAttachments);
// --- Service-key (ADR-052, этап I) ---
/**
 * ADR-052: каталог скоупов service-key + wildcard-семейства. Глобальный `*` не
 * принимается (`isKnownScope`).
 */
export const serviceScopeSchema = z
    .string()
    .min(1)
    .refine((value) => isKnownScope(value), { message: 'Неизвестный scope' });
const serviceScopesSchema = z.array(serviceScopeSchema).min(1);
/**
 * ADR-052: метаданные service-key (ответы API). Секрет (`key`) и его хеш здесь
 * отсутствуют — наружу не отдаются (в списке — `prefix`).
 */
export const serviceApiKeySchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    description: z.string().optional(),
    roles: z.array(globalRoleSchema),
    scopes: z.array(z.string().min(1)),
    allowedProjects: z.array(z.string().min(1)).optional(),
    rateLimitPerMinute: z.number().int().min(0).optional(),
    expiresAt: z.string().optional(),
    revokedAt: z.string().optional(),
    createdBy: z.string().min(1),
    createdAt: z.string().min(1),
    lastUsedAt: z.string().optional(),
    prefix: z.string().min(1),
});
/** ADR-052: тело создания service-key. `roles` не заданы — дефолт хранилища. */
export const serviceApiKeyCreateSchema = z.object({
    label: z.string().min(1),
    description: z.string().optional(),
    roles: z.array(globalRoleSchema).optional(),
    scopes: serviceScopesSchema,
    allowedProjects: z.array(z.string().min(1)).optional(),
    rateLimitPerMinute: z.number().int().min(0).optional(),
    expiresAt: z.string().optional(),
});
/**
 * ADR-052: тело обновления service-key. Все поля опциональны; `null` для
 * `description`/`allowedProjects`/`rateLimitPerMinute`/`expiresAt` — сброс.
 */
export const serviceApiKeyUpdateSchema = z.object({
    label: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    roles: z.array(globalRoleSchema).optional(),
    scopes: serviceScopesSchema.optional(),
    allowedProjects: z.array(z.string().min(1)).nullable().optional(),
    rateLimitPerMinute: z.number().int().min(0).nullable().optional(),
    expiresAt: z.string().nullable().optional(),
});
/** ADR-052: ответ создания service-key — метаданные + plaintext-секрет (единожды). */
export const serviceApiKeyCreatedSchema = serviceApiKeySchema.extend({
    key: z.string().min(1),
});
// --- Webhooks (ADR-052, этап II) ---
/** ADR-052 (этап II): паттерн события подписки — exact либо `prefix.*`. */
export const webhookEventPatternSchema = z
    .string()
    .min(1)
    .refine((value) => isWebhookEventPattern(value), { message: 'Некорректный паттерн события' });
/** ADR-052 (этап II): целевой URL — только http/https (SSRF-политика — в core). */
export const webhookUrlSchema = z
    .string()
    .url()
    .refine((value) => /^https?:\/\//i.test(value), { message: 'URL должен быть http(s)' });
/**
 * ADR-052 (этап II): метаданные подписки (ответы API). Секрет (`secret`) и его
 * хеш здесь отсутствуют — наружу не отдаются (в списке — `secretPrefix`).
 */
export const webhookSubscriptionSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    description: z.string().optional(),
    url: webhookUrlSchema,
    events: z.array(webhookEventPatternSchema),
    enabled: z.boolean(),
    secretPrefix: z.string().min(1),
    createdBy: z.string().min(1),
    createdAt: z.string().min(1),
    lastDeliveryAt: z.string().optional(),
    lastStatus: z.enum(['ok', 'failed']).optional(),
    disabledAt: z.string().optional(),
});
/** ADR-052 (этап II): тело создания подписки. `enabled` не задан — активна. */
export const webhookSubscriptionCreateSchema = z.object({
    label: z.string().min(1),
    description: z.string().optional(),
    url: webhookUrlSchema,
    events: z.array(webhookEventPatternSchema).optional(),
    enabled: z.boolean().optional(),
});
/**
 * ADR-052 (этап II): тело обновления подписки. Все поля опциональны; `null` для
 * `description` — сброс.
 */
export const webhookSubscriptionUpdateSchema = z.object({
    label: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    url: webhookUrlSchema.optional(),
    events: z.array(webhookEventPatternSchema).optional(),
    enabled: z.boolean().optional(),
});
/** ADR-052 (этап II): ответ создания/ротации подписки — метаданные + секрет (единожды). */
export const webhookSubscriptionCreatedSchema = webhookSubscriptionSchema.extend({
    secret: z.string().min(1),
});
/** ADR-052 (этап II): запись журнала доставки. */
export const webhookDeliverySchema = z.object({
    id: z.string().min(1),
    subscriptionId: z.string().min(1),
    eventType: z.string().min(1),
    status: z.enum(['ok', 'failed', 'pending']),
    attempts: z.number().int().min(0),
    responseStatus: z.number().int().optional(),
    error: z.string().optional(),
    createdAt: z.string().min(1),
    completedAt: z.string().optional(),
});
/** ADR-051/ADR-089: цель расписания (дискриминированный союз по `mode`). */
export const scheduleTargetSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('bound'), sessionId: z.string().min(1) }),
    z.object({
        mode: z.literal('ephemeral'),
        agentTemplate: z.string().optional(),
        project: z.string().optional(),
        ttl: z.string().optional(),
    }),
    z.object({ mode: z.literal('queue'), sessionId: z.string().min(1) }),
    // ADR-101: прямой запуск прогона пайплайна (событие в Router не публикуется).
    z.object({
        mode: z.literal('pipeline'),
        projectId: z.string().min(1),
        pipelineId: z.string().min(1),
        version: z.number().int().min(1).optional(),
        payload: z.record(z.string(), z.unknown()).optional(),
    }),
]);
/**
 * ADR-089: ссылка на шаг пайплайна — «эта сессия — шаг `step` пайплайна `id`».
 * Допустима только в целях `ephemeral`/`bind` (там, где решение порождает
 * сессию); в `bound`/`queue` сессия фиксирована — поле там отвергается.
 */
export const pipelineRefSchema = z.object({
    id: z.string().min(1),
    step: z.string().min(1),
});
/**
 * ADR-089: цель правила маршрутизации — дискриминированный союз по `mode`
 * (семантика та же, что у `scheduleTargetSchema`).
 *
 * `pipeline` (ссылка на шаг пайплайна) допустим только в `ephemeral`/`bind`;
 * в `bound`/`queue` сессия фиксирована, поэтому варианты **строгие**
 * (`.strict()`): лишние поля (в т.ч. `pipeline`) там **отвергаются**, а не
 * игнорируются — иначе ошибка конфигурации правила проходила бы молча.
 */
export const eventRouteTargetSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('bound'), sessionId: z.string().min(1) }).strict(),
    z.object({
        mode: z.literal('ephemeral'),
        agentTemplate: z.string().optional(),
        project: z.string().optional(),
        ttl: z.string().optional(),
        pipeline: pipelineRefSchema.optional(),
    }),
    z.object({ mode: z.literal('queue'), sessionId: z.string().min(1) }).strict(),
    // ADR-089 (auto-bind): цель закрепляет внешний адрес за сессией (binding создаёт Router).
    z.object({
        mode: z.literal('bind'),
        agentTemplate: z.string().optional(),
        project: z.string().optional(),
        pipeline: pipelineRefSchema.optional(),
    }),
]);
/** ADR-089: условие срабатывания правила (AND по заполненным полям). */
export const eventRouteMatchSchema = z.object({
    source: z.string().optional(),
    type: z.string().optional(),
    chatKey: z.string().optional(),
    threadKey: z.string().optional(),
    principalId: z.string().optional(),
    label: z.string().optional(),
    keywords: z.array(z.string()).optional(),
});
/** ADR-089: полная сущность правила маршрутизации событий (ответы API). */
export const eventRouteRuleSchema = z.object({
    id: z.string().min(1),
    name: z.string().optional(),
    priority: z.number().int().default(0),
    enabled: z.boolean().default(true),
    match: eventRouteMatchSchema,
    target: eventRouteTargetSchema,
});
/** ADR-089: вход создания/обновления правила (`upsert`) — без `id`. */
export const eventRouteRuleInputSchema = z.object({
    name: z.string().optional(),
    priority: z.number().int().default(0),
    enabled: z.boolean().default(true),
    match: eventRouteMatchSchema,
    target: eventRouteTargetSchema,
});
/**
 * ADR-089: тело upsert-запроса правила — вход + необязательный `id` (обновление
 * существующего правила; отсутствие — создание нового `rule_<uuid>`).
 */
export const eventRouteRuleUpsertSchema = eventRouteRuleInputSchema.extend({
    id: z.string().optional(),
});
/**
 * ADR-051/ADR-089: ровно одно из `cron` / `every` задано. Пустые строки
 * трактуются как отсутствие поля.
 */
function refineScheduleTrigger(val, ctx) {
    const hasCron = typeof val.cron === 'string' && val.cron.trim().length > 0;
    const hasEvery = typeof val.every === 'string' && val.every.trim().length > 0;
    if (hasCron === hasEvery) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Ровно одно из полей cron/every должно быть задано',
            path: ['cron'],
        });
    }
}
/** ADR-051/ADR-089: вход создания/обновления расписания (`ScheduleInput`). */
export const scheduleInputSchema = z
    .object({
    name: z.string().min(1),
    enabled: z.boolean().default(true),
    cron: z.string().optional(),
    every: z.string().optional(),
    target: scheduleTargetSchema,
    prompt: z.string().optional(),
    payload: z.record(z.string(), z.unknown()).optional(),
    ownerId: z.string().optional(),
    catchUp: z.enum(['skip', 'once']).optional(),
})
    .superRefine(refineScheduleTrigger);
/** ADR-051/ADR-089: полная сущность расписания (ответы API). */
export const scheduleSchema = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    enabled: z.boolean(),
    cron: z.string().optional(),
    every: z.string().optional(),
    target: scheduleTargetSchema,
    prompt: z.string().optional(),
    payload: z.record(z.string(), z.unknown()).optional(),
    ownerId: z.string().optional(),
    catchUp: z.enum(['skip', 'once']).optional(),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
    lastRunAt: z.string().optional(),
    nextRunAt: z.string().optional(),
    lastStatus: z.enum(['ok', 'error', 'skipped']).optional(),
    lastError: z.string().optional(),
})
    .superRefine(refineScheduleTrigger);
/** ADR-051: триггер срабатывания расписания в Job-истории. */
export const scheduleJobTriggerSchema = z.enum(['cron', 'manual', 'catchup']);
/** ADR-051: запись Job-истории (ответы `GET /api/v2/schedules/:id/jobs`). */
export const scheduleJobSchema = z.object({
    id: z.string().min(1),
    scheduleId: z.string().min(1),
    scheduleName: z.string(),
    trigger: scheduleJobTriggerSchema,
    status: z.enum(['ok', 'error', 'skipped']),
    startedAt: z.string().min(1),
    finishedAt: z.string().min(1),
    durationMs: z.number(),
    target: scheduleTargetSchema,
    dedupKey: z.string().optional(),
    // ADR-101: `pipeline` — пометка прямого запуска прогона (не решение Router).
    decision: z.enum(['bound', 'queue', 'ephemeral', 'bind', 'drop', 'pipeline']).optional(),
    reason: z.string().optional(),
    sessionId: z.string().optional(),
    pipelineId: z.string().optional(),
    runId: z.string().optional(),
    error: z.string().optional(),
});
/** ADR-102: семейство роллапов аналитики (`usage`/`activity`/`decision`/`prompt`). */
export const analyticsFamilySchema = z.enum(ANALYTICS_FAMILIES);
/** ADR-102: ось измерения роллапа (`day`/`user`/`project`/`model`/`agent`/`provider`). */
export const analyticsDimensionSchema = z.enum(ANALYTICS_DIMENSIONS);
/** ADR-102: сутки UTC в формате `YYYY-MM-DD`. */
export const analyticsDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** ADR-102: пред-агрегированный бакет роллапа (запись через порт хранения). */
export const analyticsRollupBucketSchema = z.object({
    family: analyticsFamilySchema,
    day: analyticsDaySchema,
    dimensions: z.record(z.string(), z.string()).prefault({}),
    measures: z.record(z.string(), z.number()),
    costByCurrency: z.record(z.string(), z.number()).optional(),
});
/** ADR-102: запрос среза/серии роллапов (только чтение). */
export const analyticsRollupQuerySchema = z.object({
    family: analyticsFamilySchema,
    from: analyticsDaySchema.optional(),
    to: analyticsDaySchema.optional(),
    dimensions: z.record(z.string(), z.string()).optional(),
    groupBy: z.array(analyticsDimensionSchema).optional(),
});
/**
 * ADR-102: строка отчёта — оси группировки + меры (сырые счётчики и
 * дериваты `ANALYTICS_DERIVED_MEASURES`). `costByCurrency` опускается, если пусто.
 */
export const analyticsReportRowSchema = z.object({
    dimensions: z.record(z.string(), z.string()).prefault({}),
    measures: z.record(z.string(), z.number()),
    costByCurrency: z.record(z.string(), z.number()).optional(),
});
/** ADR-102: отчёт по семейству за период (только чтение, данные без i18n). */
export const analyticsReportSchema = z.object({
    report: analyticsFamilySchema,
    from: analyticsDaySchema,
    to: analyticsDaySchema,
    groupBy: z.array(analyticsDimensionSchema),
    rows: z.array(analyticsReportRowSchema),
});
/** ADR-102: формат выгрузки отчёта (`csv`/`json`; только данные). */
export const analyticsExportFormatSchema = z.enum(ANALYTICS_EXPORT_FORMATS);
/** ADR-102: сводка инстанса за период — агрегаты `usage`/`activity`/`decision`. */
export const analyticsSummarySchema = z.object({
    from: analyticsDaySchema,
    to: analyticsDaySchema,
    usage: z.record(z.string(), z.number()),
    activity: z.record(z.string(), z.number()),
    decision: z.record(z.string(), z.number()),
    costByCurrency: z.record(z.string(), z.number()).optional(),
});
// --- Медиа-часть сообщения (ADR-115) ---
/** Медиа-типы части сообщения (ADR-115) — вложение. */
export const mediaPartTypeSchema = z.enum(['image', 'audio', 'video', 'file']);
/**
 * ADR-115 §«Модель контента»: данные медиа-части — ссылка на blob в
 * blob-хранилище + метаданные. Обязательны `blobPath`/`sha256`/`mime`/`size`;
 * остальное — необязательные метаданные (имя, размеры, длительность,
 * текстовая расшифровка).
 */
export const mediaPartSchema = z.object({
    blobPath: z.string().min(1),
    sha256: z.string().min(1),
    mime: z.string().min(1),
    size: z.number().int().min(0),
    name: z.string().optional(),
    width: z.number().int().min(0).optional(),
    height: z.number().int().min(0).optional(),
    durationMs: z.number().int().min(0).optional(),
    transcript: z.string().optional(),
});
